import React from 'react';
import { createRoot } from 'react-dom/client';
import CustomAlert from '../components/ui/CustomAlert';

// Global alert state
let alertContainer = null;
let currentRoot = null;

// Create container for alerts if it doesn't exist
const createAlertContainer = () => {
  if (!alertContainer) {
    alertContainer = document.createElement('div');
    alertContainer.id = 'custom-alert-container';
    document.body.appendChild(alertContainer);
  }
  return alertContainer;
};

// Remove alert container
const removeAlertContainer = () => {
  if (alertContainer) {
    document.body.removeChild(alertContainer);
    alertContainer = null;
  }
};

// Custom alert function
export const showCustomAlert = ({
  title = "Alert",
  message = "An alert has occurred.",
  type = "info", // "info", "success", "warning", "error"
  showCloseButton = true,
  autoClose = false,
  autoCloseDelay = 5000,
  onConfirm = null,
  confirmText = "OK",
  showConfirmButton = true
}) => {
  return new Promise((resolve) => {
    const container = createAlertContainer();
    
    const handleClose = () => {
      console.log('handleClose called');
      // Clean up the React component
      if (currentRoot) {
        console.log('Unmounting currentRoot');
        currentRoot.unmount();
        currentRoot = null;
      }
      resolve();
    };

    const handleConfirm = () => {
      console.log('handleConfirm called in utility');
      if (onConfirm) {
        onConfirm();
      }
      handleClose();
    };

    const AlertComponent = () => {
      const [isVisible, setIsVisible] = React.useState(true);
      
      const handleCloseLocal = () => {
        console.log('handleCloseLocal called');
        setIsVisible(false);
        handleClose();
      };

      const handleConfirmLocal = () => {
        console.log('handleConfirmLocal called');
        if (onConfirm) {
          onConfirm();
        }
        handleCloseLocal();
      };

      if (!isVisible) return null;

      return (
        <CustomAlert
          isOpen={true}
          onClose={handleCloseLocal}
          title={title}
          message={message}
          type={type}
          showCloseButton={showCloseButton}
          autoClose={autoClose}
          autoCloseDelay={autoCloseDelay}
          onConfirm={handleConfirmLocal}
          confirmText={confirmText}
          showConfirmButton={showConfirmButton}
        />
      );
    };

    currentRoot = createRoot(container);
    currentRoot.render(<AlertComponent />);
  });
};

// Convenience functions for different alert types
export const showInfoAlert = (message, title = "Information") => {
  return showCustomAlert({
    title,
    message,
    type: "info"
  });
};

export const showSuccessAlert = (message, title = "Success") => {
  return showCustomAlert({
    title,
    message,
    type: "success"
  });
};

export const showWarningAlert = (message, title = "Warning") => {
  return showCustomAlert({
    title,
    message,
    type: "warning"
  });
};

export const showErrorAlert = (message, title = "Error") => {
  return showCustomAlert({
    title,
    message,
    type: "error"
  });
};

// Replace browser's default alert
export const replaceBrowserAlert = () => {
  const originalAlert = window.alert;
  
  window.alert = (message, title = "Alert") => {
    return showCustomAlert({
      title,
      message,
      type: "info"
    });
  };
  
  // Return function to restore original alert
  return () => {
    window.alert = originalAlert;
  };
};

// Cleanup function
export const cleanupAlerts = () => {
  if (currentRoot) {
    currentRoot.unmount();
    currentRoot = null;
  }
  removeAlertContainer();
};

// Auto-cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanupAlerts);
} 